import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AdvanceButton from "@/components/AdvanceButton";
import StatusBadge from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Camera, CameraOff, Search } from "lucide-react";
import { toast } from "sonner";

function parseScan(text) {
  const m = text.match(/\/rework\/([a-f0-9]{24})/i);
  if (m) return { id: m[1] };
  // Groundwork for Cabinet Vision labels: "JOB1042|ABC-100" or "JOB1042 ABC-100"
  const parts = text.split(/[|;,\s]+/).filter(Boolean);
  if (parts.length >= 2) return { job: parts[0].toUpperCase(), part: parts[1].toUpperCase() };
  return null;
}

export default function Scan() {
  const nav = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState({ job: "", part: "" });
  const [found, setFound] = useState(null);
  const scannerRef = useRef(null);

  const resolve = async (parsed) => {
    if (!parsed) { toast.error("Unrecognised code"); return; }
    try {
      if (parsed.id) {
        const { data } = await api.get(`/reworks/${parsed.id}`);
        setFound(data);
        return;
      }
      const { data } = await api.get("/reworks", { params: { search: parsed.job, active_only: true } });
      const hit = data.find((r) => r.job_number === parsed.job && r.part_number === parsed.part);
      if (hit) setFound(hit);
      else {
        toast.info("No active rework for that label — start a new request");
        nav(`/new?job=${encodeURIComponent(parsed.job)}&part=${encodeURIComponent(parsed.part)}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Lookup failed");
    }
  };

  const start = async () => {
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const s = new Html5Qrcode("scan-region");
      scannerRef.current = s;
      await s.start({ facingMode: "environment" }, { fps: 8, qrbox: 220 }, (text) => { stop(); resolve(parseScan(text)); }, () => {});
      setScanning(true);
    } catch (e) {
      toast.error("Camera unavailable — use manual entry below");
    }
  };

  const stop = async () => {
    try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch (e) { console.error("Scanner stop failed", e); }
    setScanning(false);
  };

  useEffect(() => () => { stop(); }, []);

  return (
    <div>
      <PageHeader title="Scan Station" subtitle="Scan the rack label to update a part's stage in one tap." testid="scan-header" />
      <div className="p-6 md:p-10 max-w-2xl space-y-6">
        <div className="border border-[#e6e5df] bg-white rounded-sm p-6 space-y-4">
          <div id="scan-region" className={`rounded-sm overflow-hidden bg-[#232826] ${scanning ? "min-h-[260px]" : "h-0"}`} />
          <Button onClick={scanning ? stop : start} data-testid="scan-toggle"
            className="w-full h-14 bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase tracking-widest text-sm">
            {scanning ? <><CameraOff size={16} className="mr-2" /> Stop camera</> : <><Camera size={16} className="mr-2" /> Start scanning</>}
          </Button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); resolve({ job: manual.job.trim().toUpperCase(), part: manual.part.trim().toUpperCase() }); }}
          className="border border-[#e6e5df] bg-white rounded-sm p-6 space-y-3" data-testid="scan-manual-form">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono">Manual lookup</div>
          <div className="grid grid-cols-2 gap-3">
            <Input value={manual.job} onChange={(e) => setManual({ ...manual, job: e.target.value })} placeholder="Job number" required data-testid="scan-job" className="h-12 bg-white border-[#cfcfc9] rounded-sm uppercase text-lg" />
            <Input value={manual.part} onChange={(e) => setManual({ ...manual, part: e.target.value })} placeholder="Part number" required data-testid="scan-part" className="h-12 bg-white border-[#cfcfc9] rounded-sm uppercase text-lg" />
          </div>
          <Button type="submit" variant="secondary" data-testid="scan-lookup" className="w-full h-12 rounded-sm bg-[#e6e5df] hover:bg-[#cfcfc9] text-[#232826] border border-[#cfcfc9] uppercase text-xs tracking-widest">
            <Search size={14} className="mr-2" /> Find part
          </Button>
        </form>

        {found && (
          <div className="border-2 border-[#424e43] bg-white rounded-sm p-6 space-y-4" data-testid="scan-result">
            <div className="flex items-start justify-between">
              <div>
                <div className="mono text-2xl text-[#232826]">{found.job_number}</div>
                <div className="mono text-lg text-[#424e43]">{found.part_number}</div>
                <div className="text-sm text-[#6b6a68] mt-1">{found.rework_reason} — {found.description}</div>
              </div>
              <StatusBadge status={found.status} testid="scan-result-status" />
            </div>
            <AdvanceButton rework={found} size="lg" testid="scan-advance" onDone={(d) => setFound(d)} />
            <button onClick={() => nav(`/rework/${found.id}`)} className="text-xs text-[#647265] hover:underline" data-testid="scan-open-detail">Open full record →</button>
          </div>
        )}
      </div>
    </div>
  );
}
